import { useEffect, useMemo, useState } from "react";
import { Polygon, Marker, Polyline } from "react-leaflet";
import L from "leaflet";
import { api, apiError } from "@/lib/api";
import LeafletMap, { FitBounds } from "@/components/map/LeafletMap";
import { StatusChip } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Route as RouteIcon, Plus, Trash2, Pencil, X, Loader2, Undo2, MousePointerClick, Send, Info } from "lucide-react";
import { toast } from "sonner";

const ROUTE_TYPES = [
  { key: "primary", label: "Primary trail" },
  { key: "connector", label: "Connector" },
  { key: "detour", label: "Detour" },
  { key: "attraction", label: "Attraction spur" },
];
const TYPE_COLOR = { primary: "#22D3EE", connector: "#10B981", detour: "#8B5CF6", attraction: "#F59E0B" };

function inPolygon(lat, lng, poly) {
  if (!poly || poly.length < 3) return true;
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const yi = poly[i][0], xi = poly[i][1], yj = poly[j][0], xj = poly[j][1];
    if (((yi > lat) !== (yj > lat)) && (lng < ((xj - xi) * (lat - yi)) / ((yj - yi) || 1e-12) + xi)) inside = !inside;
  }
  return inside;
}
const dot = (color) => L.divIcon({ className: "", iconSize: [16, 16], iconAnchor: [8, 8], html: `<div style="width:16px;height:16px;border-radius:9999px;background:${color};border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.3);cursor:grab"></div>` });
const EMPTY = { id: null, name: "", route_type: "primary", path: [], linked_stop_id: "" };
const isLive = (r) => r.approval_status === "approved" && r.active !== false;
const canSubmit = (r) => ["draft", "changes_requested", "rejected"].includes(r.approval_status);

export default function ShopRoutesPanel() {
  const [zones, setZones] = useState([]);
  const [zoneId, setZoneId] = useState("");
  const [routes, setRoutes] = useState([]);
  const [attractions, setAttractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const zone = useMemo(() => zones.find((z) => z.id === zoneId), [zones, zoneId]);

  const loadZones = async () => {
    setLoading(true);
    try { const { data } = await api.get("/my/zones"); setZones(data); if (data.length && !zoneId) setZoneId(data[0].id); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const loadRoutes = async () => {
    if (!zoneId) return setRoutes([]);
    try { const { data } = await api.get("/shop/routes", { params: { zone_id: zoneId } }); setRoutes(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const loadAttractions = async () => {
    if (!zoneId) return setAttractions([]);
    try { const { data } = await api.get("/content", { params: { kind: "attraction", zone_id: zoneId } }); setAttractions(data.filter((c) => c.kind === "attraction")); }
    catch { setAttractions([]); }
  };
  useEffect(() => { loadZones(); }, []);
  useEffect(() => { loadRoutes(); loadAttractions(); setEditing(null); }, [zoneId]);

  const poly = zone?.polygon || [];
  const center = poly.length ? poly[0] : [41.75, -71.3];
  const geoOk = (p) => p.every((pt) => inPolygon(pt[0], pt[1], poly));
  const badGeo = editing && editing.path.length > 0 && !geoOk(editing.path);

  const mapClick = (lat, lng) => { if (editing) setEditing((s) => ({ ...s, path: [...s.path, [lat, lng]] })); };
  const startNew = () => setEditing({ ...EMPTY });
  const startEdit = (r) => setEditing({ id: r.id, name: r.name || "", route_type: r.route_type || "primary", path: r.coordinates || [], linked_stop_id: r.linked_stop_id || "" });

  const save = async () => {
    const f = editing;
    if (!f.name.trim()) return toast.error("Route name is required");
    if (f.path.length < 2) return toast.error("Click at least 2 points on the map to draw the route");
    if (!geoOk(f.path)) return toast.error("Every point must stay inside your service area");
    setSaving(true);
    try {
      const body = { name: f.name.trim(), route_type: f.route_type, zone_id: zoneId, coordinates: f.path, linked_stop_id: f.route_type === "attraction" ? (f.linked_stop_id || null) : null };
      if (f.id) { await api.put(`/shop/routes/${f.id}`, body); toast.success("Route saved"); }
      else { await api.post("/shop/routes", body); toast.success("Draft created — submit it when you're ready for review"); }
      setEditing(null); loadRoutes();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const submit = async (id) => { try { await api.post(`/shop/routes/${id}/submit`); toast.success("Submitted for admin review"); loadRoutes(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const del = async (id) => { try { await api.delete(`/shop/routes/${id}`); toast.success("Deleted"); loadRoutes(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!zones.length) return <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground" data-testid="routes-no-zones">No service area assigned to your shop yet. Velopaths will assign your zone(s) soon.</div>;

  const bounds = poly.length >= 2 ? poly : null;

  return (
    <div className="space-y-4" data-testid="shop-routes-panel">
      <div className="flex items-center gap-2">
        <RouteIcon className="w-5 h-5 text-primary" />
        <div><h2 className="font-heading text-lg font-bold">Routes</h2><p className="text-sm text-muted-foreground">Draw routes inside your zone. Each one is reviewed by Velopaths before it reaches riders.</p></div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <Label>Zone</Label>
          <Select value={zoneId} onValueChange={setZoneId}>
            <SelectTrigger data-testid="routes-zone-select"><SelectValue /></SelectTrigger>
            <SelectContent>{zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.name}{z.bike_path_name ? ` · ${z.bike_path_name}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="ml-auto">
          {!editing
            ? <Button onClick={startNew} data-testid="routes-new-button"><Plus className="w-4 h-4 mr-1" />New route</Button>
            : <Button variant="ghost" onClick={() => setEditing(null)} data-testid="routes-cancel-button"><X className="w-4 h-4 mr-1" />Cancel</Button>}
        </div>
      </div>

      {editing && (
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3" data-testid="routes-form">
          <div className="text-sm text-muted-foreground flex items-center gap-2"><MousePointerClick className="w-4 h-4 text-primary shrink-0" />Click the map to add points, then drag any point to refine. The route must stay inside your zone.</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="routes-name-input" placeholder="e.g. Harbor Loop" /></div>
            <div><Label>Type</Label>
              <Select value={editing.route_type} onValueChange={(v) => setEditing({ ...editing, route_type: v })}>
                <SelectTrigger data-testid="routes-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROUTE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {editing.route_type === "attraction" && (
            <div data-testid="routes-attraction-link-wrap">
              <Label>Links to attraction</Label>
              <Select value={editing.linked_stop_id || "none"} onValueChange={(v) => setEditing({ ...editing, linked_stop_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="routes-attraction-select"><SelectValue placeholder="None (always shown)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (always shown)</SelectItem>
                  {attractions.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">{attractions.length ? "Riders reveal this spur by tapping the linked attraction (\"Show route\"). Leave as None to always draw it." : "Create an attraction in this zone first to link it. Leave as None to always draw this spur."}</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing((s) => ({ ...s, path: s.path.slice(0, -1) }))} disabled={!editing.path.length} data-testid="routes-undo-point"><Undo2 className="w-4 h-4 mr-1" />Undo point</Button>
            <span className="text-sm text-muted-foreground" data-testid="routes-point-count">{editing.path.length} point{editing.path.length === 1 ? "" : "s"}</span>
            <span className={`text-xs ${badGeo ? "text-destructive" : "text-muted-foreground"}`} data-testid="routes-geo-status">{editing.path.length ? (badGeo ? "Some points are outside your zone" : "All points inside your zone") : "No points yet"}</span>
          </div>
          <div className="flex justify-end"><Button onClick={save} disabled={saving} data-testid="routes-save-button">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save draft</Button></div>
        </div>
      )}

      <LeafletMap height={420} onClick={editing ? mapClick : undefined} center={center}>
        {poly.length >= 3 && <Polygon positions={poly} pathOptions={{ color: "#EF4444", fillColor: "#FACC15", fillOpacity: 0.15, weight: 2 }} />}
        {bounds && <FitBounds bounds={bounds} />}
        {!editing && routes.map((r) => r.coordinates?.length >= 2 && (
          <Polyline key={r.id} positions={r.coordinates} pathOptions={{ color: TYPE_COLOR[r.route_type] || "#22D3EE", opacity: isLive(r) ? 1 : 0.5, dashArray: isLive(r) ? null : "6" }} />
        ))}
        {editing && editing.path.length >= 2 && <Polyline positions={editing.path} pathOptions={{ color: badGeo ? "#EF4444" : "#F59E0B" }} />}
        {editing && editing.path.map((p, i) => (
          <Marker key={i} position={p} draggable icon={dot(badGeo ? "#EF4444" : "#F59E0B")}
            eventHandlers={{ dragend: (e) => { const { lat, lng } = e.target.getLatLng(); setEditing((s) => { const path = [...s.path]; path[i] = [lat, lng]; return { ...s, path }; }); } }} />
        ))}
      </LeafletMap>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="routes-list">
        {routes.length === 0 ? <div className="text-sm text-muted-foreground">No routes yet. Click “New route” to draw your first one.</div> : routes.map((r) => (
          <div key={r.id} className="rounded-2xl border border-border bg-card p-4 space-y-2" data-testid={`route-card-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-heading font-bold flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: TYPE_COLOR[r.route_type] || "#22D3EE" }} />{r.name}</div>
                <div className="text-xs text-muted-foreground capitalize">{r.route_type} · {r.coordinates?.length || 0} pts{r.linked_stop_id ? ` · links to ${attractions.find((a) => a.id === r.linked_stop_id)?.name || "attraction"}` : ""}</div>
              </div>
              <StatusChip status={r.approval_status} live={isLive(r)} testId={`route-status-${r.id}`} />
            </div>
            {r.approval_status === "changes_requested" && r.change_request_note && (
              <div className="text-xs rounded-md bg-orange-500/10 border border-orange-500/20 px-2 py-1.5 text-orange-300 flex gap-1.5" data-testid={`route-change-note-${r.id}`}><Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />{r.change_request_note}</div>
            )}
            {r.approval_status === "rejected" && r.rejection_reason && (
              <div className="text-xs rounded-md bg-red-500/10 border border-red-500/20 px-2 py-1.5 text-red-300 flex gap-1.5" data-testid={`route-reject-reason-${r.id}`}><Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />{r.rejection_reason}</div>
            )}
            <div className="flex items-center justify-between pt-1">
              <div className="flex gap-1">
                {canSubmit(r) && <Button size="sm" variant="outline" onClick={() => submit(r.id)} data-testid={`route-submit-${r.id}`}><Send className="w-3.5 h-3.5 mr-1" />Submit</Button>}
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => startEdit(r)} data-testid={`route-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="ghost" size="sm" className="text-destructive" data-testid={`route-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete "{r.name}"?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(r.id)} data-testid={`route-confirm-delete-${r.id}`}>Delete</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            {r.approval_status === "approved" && (
              <p className="text-[11px] text-muted-foreground">Editing a live route sends it back for re-approval and hides it from riders until approved again.</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
